/**
 * When is the next midnight in a given time zone, as a delay in ms?
 *
 * The version policy is re-checked once a night rather than on a short
 * interval: a check that fires while someone is working can only ever
 * interrupt them, and the two moments that cost nothing are returning
 * to the tab and the small hours.
 *
 * The account's own zone, not the browser's — midnight in New York is
 * nine in the evening in California, which is still someone's working
 * day. `DEFAULT_ACCOUNT_TIME_ZONE` covers the stretch before sign-in,
 * when there is no account to ask.
 *
 * A sleeping laptop will not fire the timer on time; it fires late, on
 * wake. That is fine — the focus check runs first thing in the morning
 * anyway, so this is the backstop for a tab nobody touches.
 */
import { DEFAULT_ACCOUNT_TIME_ZONE } from './zonedDay';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The zone's current wall-clock parts for an instant.
 * Returns null when the zone is not one the runtime knows.
 */
const wallClockParts = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const read = type => Number(parts.find(part => part.type === type)?.value);
    const hour = read('hour') % 24; // 24:00 is midnight in some locales
    const minute = read('minute');
    const second = read('second');
    if ([hour, minute, second].some(Number.isNaN)) return null;
    return { hour, minute, second };
  } catch {
    return null;
  }
};

/**
 * Milliseconds from `from` until the next midnight in `timeZone`.
 *
 * Falls back to a flat 24 hours when the zone cannot be read, so the
 * nightly check still happens — just not aligned to a local midnight.
 *
 * @param {string} timeZone IANA name
 * @param {Date} [from] defaults to now
 * @returns {number} delay in ms, always > 0
 */
export const msUntilNextMidnight = (timeZone = DEFAULT_ACCOUNT_TIME_ZONE, from = new Date()) => {
  const parts = wallClockParts(from, timeZone);
  if (!parts) return DAY_MS;
  const elapsed =
    ((parts.hour * 60 + parts.minute) * 60 + parts.second) * 1000 + from.getMilliseconds();
  const remaining = DAY_MS - elapsed;
  // Exactly at midnight, wait a whole day rather than firing twice.
  return remaining <= 0 ? DAY_MS : remaining;
};

export default msUntilNextMidnight;
